/**
 * Participant Service
 * API calls for participant dashboard, sessions, activities, certificates, and analytics
 */

import api from './api';

const participantService = {
  // ==================== DASHBOARD ====================

  /**
   * Get participant dashboard statistics
   * @returns {Promise} Dashboard stats including sessions, certificates, scores, and hours
   */
  getDashboardStats: async () => {
    return api.get('/participant/dashboard');
  },

  // ==================== SESSIONS ====================

  /**
   * Get participant's sessions
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (all, completed, upcoming, live)
   * @param {string} params.search - Search by name or code
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of sessions with pagination and stats
   */
  getSessions: async (params = {}) => {
    return api.get('/participant/sessions', params);
  },

  /**
   * Get single session details
   * @param {string} sessionId - Session ID
   * @returns {Promise} Session details with activities and user responses
   */
  getSession: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}`);
  },

  /**
   * Join a session with code (authenticated)
   * @param {Object} joinData - Join data
   * @param {string} joinData.code - Session code
   * @param {string} joinData.name - Participant display name (optional)
   * @param {string} joinData.email - Participant email (optional)
   * @param {string} joinData.organization - Participant organization (optional)
   * @returns {Promise} Join confirmation with session info
   */
  joinSession: async (joinData) => {
    return api.post('/participant/sessions/join', joinData);
  },

  /**
   * Join a session publicly (no auth required - for QR code/session code joiners)
   * @param {Object} joinData - Join data
   * @param {string} joinData.code - Session code (required)
   * @param {string} joinData.name - Participant name (required)
   * @param {string} joinData.email - Participant email (optional)
   * @param {string} joinData.organization - Participant organization (optional)
   * @param {string} joinData.existingParticipantId - Existing participant ID for rejoining (optional)
   * @returns {Promise} Join confirmation with participantId, sessionId, sessionName, isMultiDay, participantCode
   */
  joinSessionPublic: async (joinData) => {
    return api.post('/participant/sessions/join-public', joinData);
  },

  /**
   * Recover participant session from httpOnly cookie (Safari/iOS fallback)
   * Called when localStorage/sessionStorage is cleared by Safari ITP
   * @returns {Promise} Recovered participant data or 404 if no cookie
   */
  recoverSession: async () => {
    return api.get('/participant/recover-session');
  },

  /**
   * Check if a returning participant exists for a session code (via cookie)
   * @param {string} sessionCode - The session code entered by participant
   * @returns {Promise} { returning: true/false, participant, session }
   */
  checkReturningParticipant: async (sessionCode) => {
    return api.get(`/participant/check-returning/${sessionCode}`);
  },

  /**
   * Rejoin a multi-day session using participant code
   * @param {Object} rejoinData - Rejoin data
   * @param {string} rejoinData.sessionCode - Session code (required)
   * @param {string} rejoinData.participantCode - Participant code (required)
   * @returns {Promise} Rejoin confirmation with participantId, sessionId, etc.
   */
  rejoinByCode: async (rejoinData) => {
    return api.post('/participant/sessions/rejoin-by-code', rejoinData);
  },

  /**
   * Leave a session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Success response
   *
   * NOTE: We preserve ALL participant data so that participants can:
   * 1. Rejoin paused sessions (multi-day training) without re-entering info
   * 2. View their report after session ends
   * 3. Auto-fill form if they visit join page again
   */
  leaveSession: async (sessionId) => {
    // PRESERVE all participant data in localStorage
    // - participantData: for auto-reconnect and pre-filling forms
    // - current_participant: for quick access to participant info
    // - participant_${sessionId}: for session-specific identification
    //
    // Data will be overwritten when joining a new session

    return api.post(`/participant/sessions/${sessionId}/leave`, {});
  },

  /**
   * Clear participant data from localStorage
   * @param {boolean} clearAll - If true, clears ALL participant data including session history
   *
   * Use clearAll=false (default) to preserve session history for viewing reports
   * Use clearAll=true only when user explicitly wants to clear all their data
   */
  clearParticipantData: (clearAll = false) => {
    if (clearAll) {
      // Clear everything - used when user explicitly clears all data
      localStorage.removeItem('participantData');
      localStorage.removeItem('current_participant');

      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('participant_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
    // If clearAll is false, don't clear anything - preserve for rejoining/viewing reports
  },

  // ==================== ACTIVITIES ====================

  /**
   * Get active activities for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise} List of currently active activities
   */
  getActiveActivities: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}/activities`);
  },

  /**
   * Submit response to an activity
   * @param {string} sessionId - Session ID
   * @param {string} activityId - Activity ID
   * @param {Object} responseData - Response data
   * @param {any} responseData.response - Activity response (varies by type)
   * @returns {Promise} Submission confirmation
   */
  submitActivityResponse: async (sessionId, activityId, responseData) => {
    return api.post(`/participant/sessions/${sessionId}/activities/${activityId}/respond`, responseData);
  },

  /**
   * Get activity results (after completion)
   * @param {string} sessionId - Session ID
   * @param {string} activityId - Activity ID
   * @returns {Promise} Activity results with user response and aggregated data
   */
  getActivityResults: async (sessionId, activityId) => {
    return api.get(`/participant/sessions/${sessionId}/activities/${activityId}/results`);
  },

  // ==================== WORKBOOKS ====================

  /**
   * Get session workbooks
   * @param {string} sessionId - Session ID
   * @returns {Promise} List of workbooks for the session
   */
  getSessionWorkbooks: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}/workbooks`);
  },

  /**
   * Track workbook view
   * @param {string} sessionId - Session ID
   * @param {string} workbookId - Workbook ID
   * @param {string} workbookName - Workbook name
   * @returns {Promise} Updated workbook views
   */
  trackWorkbookView: async (sessionId, workbookId, workbookName) => {
    return api.post(`/participant/sessions/${sessionId}/workbook-view`, { workbookId, workbookName });
  },

  /**
   * Get workbook views for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Workbook views data
   */
  getWorkbookViews: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}/workbook-views`);
  },

  // ==================== NOTES ====================

  /**
   * Save notes for a session
   * @param {string} sessionId - Session ID
   * @param {string} notes - Notes content
   * @returns {Promise} Success response
   */
  saveNotes: async (sessionId, notes) => {
    return api.post(`/participant/sessions/${sessionId}/notes`, { notes });
  },

  /**
   * Get saved notes for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Saved notes content
   */
  getNotes: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}/notes`);
  },

  // ==================== CERTIFICATES ====================

  /**
   * Get participant's certificates
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of certificates with pagination
   */
  getCertificates: async (params = {}) => {
    return api.get('/participant/certificates', params);
  },

  /**
   * Get single certificate details
   * @param {string} certificateId - Certificate/Session ID
   * @returns {Promise} Certificate details with performance data
   */
  getCertificate: async (certificateId) => {
    return api.get(`/participant/certificates/${certificateId}`);
  },

  // ==================== ACTIVITY HISTORY ====================

  /**
   * Get participant's activity history
   * @param {Object} params - Query parameters
   * @param {string} params.type - Filter by activity type (poll, quiz, wordCloud, etc.)
   * @param {string} params.sessionId - Filter by session
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} Activity history with stats and pagination
   */
  getActivityHistory: async (params = {}) => {
    return api.get('/participant/activity-history', params);
  },

  // ==================== REPORTS & ANALYTICS ====================

  /**
   * Get session report for participant
   * @param {string} sessionId - Session ID
   * @returns {Promise} Session report with performance metrics
   */
  getSessionReport: async (sessionId) => {
    return api.get(`/participant/sessions/${sessionId}/report`);
  },

  /**
   * Get individual participant report for a session
   * @param {string} sessionId - Session ID
   * @param {string} participantId - Participant ID (from localStorage or URL)
   * @returns {Promise} Individual report with participant data and responses
   */
  getParticipantReport: async (sessionId, participantId) => {
    return api.get(`/participant/sessions/${sessionId}/report/${participantId}`);
  },

  /**
   * Get participant's overall analytics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (7d, 30d, 90d, all)
   * @returns {Promise} Analytics data with trends and breakdowns
   */
  getAnalytics: async (params = {}) => {
    return api.get('/participant/analytics', params);
  },

  // ==================== OVERALL SESSION FEEDBACK ====================

  /**
   * Submit overall session feedback
   * @param {string} sessionId - Session ID
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.participantId - Participant ID
   * @param {string} feedbackData.participantName - Participant name
   * @param {string} feedbackData.participantEmail - Participant email
   * @param {number} feedbackData.rating - Rating (1-5)
   * @param {string} feedbackData.comment - Optional comment
   * @returns {Promise} Success response
   */
  submitOverallFeedback: async (sessionId, feedbackData) => {
    return api.post(`/sessions/${sessionId}/overall-feedback/submit`, feedbackData);
  },

  /**
   * Get overall feedback launch status for participant (public).
   * Returns launch flag and whether this participant already submitted.
   */
  getOverallFeedbackStatus: async (sessionId, { participantId, participantEmail, participantName } = {}) => {
    const params = new URLSearchParams();
    if (participantId) params.append('participantId', participantId);
    if (participantEmail) params.append('participantEmail', participantEmail);
    if (participantName) params.append('participantName', participantName);
    const qs = params.toString();
    return api.get(`/sessions/${sessionId}/overall-feedback/status${qs ? `?${qs}` : ''}`);
  },
};

export default participantService;
